import { useEffect, useMemo, useReducer } from "react";

import useIsMounted from "./useIsMounted";

const allFetched = pagination => (
  pagination && pagination.currentPage >= pagination.totalPages
);

const indexById = entities => entities.reduce(
  (accum, entity) => ({ ...accum, [entity.id]: entity }), {}
);

export const onFetchAll = (entity, onFetchPage) => onFetchPage(1).then(initial => {
  if (initial.pagination.totalPages <= 1) {
    return initial;
  }

  const promises = [];
  for (let page = 2; page <= initial.pagination.totalPages; page += 1) {
    promises.push(onFetchPage(page));
  }

  return Promise.all(promises).then(responses => responses.reduce(
    (accum, response) => ({
      ...response,
      [entity]: [...accum[entity], ...response[entity]]
    }),
    initial
  ));
});

const onFetchIds = (entityIds, onFetchEntity) => {
  const promises = entityIds.map(entityId => new Promise((resolve, reject) => {
    onFetchEntity(entityId).then(resolve).catch(error => {
      if (error.status === 404) {
        resolve(null);
      } else {
        reject(error);
      }
    });
  }));

  return Promise.all(promises).then(responses => responses.filter(Boolean));
};

const initialState = {
  entities: {},
  fetchingAll: false,
  fetchingAutocomplete: null,
  fetchingIds: null,
  fetchingPage: null,
  pagination: null
};

const reducer = (state, action) => {
  switch (action.type) {
    case "fetchAll":
      if (allFetched(state.pagination)) {
        return state;
      }

      return { ...state, fetchingAll: true };
    case "fetchAllSuccess":
      return {
        ...state,
        entities: { ...state.entities, ...indexById(action.entities) },
        fetchingAll: false,
        pagination: action.pagination
      };
    case "fetchAutocomplete":
      if (allFetched(state.pagination)) {
        return state;
      }

      return { ...state, fetchingAutocomplete: action.query };
    case "fetchAutocompleteSuccess":
      return {
        ...state,
        entities: { ...state.entities, ...indexById(action.entities) },
        fetchingAutocomplete: null
      };
    case "fetchIds": {
      if (!state.fetchingIds) {
        return { ...state, fetchingIds: action.entityIds };
      }

      if (state.fetchingIds.every(entityId => action.entityIds.includes(entityId))) {
        return state;
      }

      const fetchingIds = state.fetchingIds.concat(action.entityIds).filter(
        (entityId, index, array) => array.indexOf(entityId) === index
      );

      return { ...state, fetchingIds };
    }
    case "fetchIdsSuccess":
      return {
        ...state,
        entities: { ...state.entities, ...indexById(action.entities) },
        fetchingIds: null
      };
    case "fetchNextPage":
      if (allFetched(state.pagination)) {
        return state;
      }

      return {
        ...state,
        fetchingPage: state.pagination ? state.pagination.currentPage + 1 : 1
      };
    case "fetchPageSuccess":
      return {
        ...state,
        entities: { ...state.entities, ...indexById(action.entities) },
        fetchingPage: null,
        pagination: action.pagination
      };
    default:
      throw new Error();
  }
};

const usePagedEntity = (entityName, onFetchPage, onFetchEntity, onFetchAutocomplete) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const isMounted = useIsMounted();

  // When `fetchingAll` is set to `true`, it will trigger an effect that will
  // go to the API and fetch every instance of the entity using the provided
  // `onFetchPage` call. It will automatically handle pagination, and after
  // having fetched every page will load them into the overall entity state.
  useEffect(
    () => {
      if (state.fetchingAll) {
        onFetchAll(`${entityName}s`, onFetchPage).then(response => {
          if (isMounted) {
            dispatch({
              type: "fetchAllSuccess",
              entities: response[`${entityName}s`],
              pagination: response.pagination
            });
          }
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, entityName, isMounted, state.fetchingAll]
  );

  // When `fetchingPage` is set to a page number, it will trigger an effect
  // that will go to the API and fetch a given page of instances of the entity
  // using the provided `onFetchPage` call. It will then load them into the
  // overall entity state.
  useEffect(
    () => {
      if (state.fetchingPage) {
        onFetchPage(state.fetchingPage).then(response => {
          if (isMounted) {
            dispatch({
              type: "fetchPageSuccess",
              entities: response[`${entityName}s`],
              pagination: response.pagination
            });
          }
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, entityName, isMounted, state.fetchingPage]
  );

  // When `fetchingIds` is set to an array of entity ids, it will trigger an
  // effect that will go to the API and fetch each instance of the entity
  // corresponding to the given ids using the provided `onFetchEntity` call. It
  // will then load them into the overall entity state.
  useEffect(
    () => {
      if (state.fetchingIds) {
        onFetchIds(state.fetchingIds, onFetchEntity).then(responses => {
          if (isMounted) {
            dispatch({
              type: "fetchIdsSuccess",
              entities: responses.map(response => response[entityName])
            });
          }
        });
      }
    },
    [dispatch, entityName, isMounted, onFetchEntity, state.fetchingIds]
  );

  // When `fetchingAutocomplete` is set to a string query, it will trigger an
  // effect that will go to the API and fetch the autocompleted instances for
  // the entity using the `onFetchAutocomplete` call. It will then load them
  // into the overall entity state.
  useEffect(
    () => {
      if (state.fetchingAutocomplete) {
        onFetchAutocomplete(state.fetchingAutocomplete).then(response => {
          if (isMounted) {
            dispatch({
              type: "fetchAutocompleteSuccess",
              entities: response[`${entityName}s`]
            });
          }
        });
      }
    },
    [dispatch, entityName, isMounted, onFetchAutocomplete, state.fetchingAutocomplete]
  );

  return useMemo(
    () => {
      const capitalized = `${entityName.charAt(0).toUpperCase()}${entityName.slice(1)}`;

      return {
        [`all${capitalized}sFetched`]: allFetched(state.pagination),
        [`${entityName}s`]: state.entities,
        [`onAll${capitalized}sRequested`]: () => dispatch({ type: "fetchAll" }),
        [`onAutocomplete${capitalized}s`]: query => dispatch({ type: "fetchAutocomplete", query }),
        [`on${capitalized}sRequested`]: entityIds => dispatch({ type: "fetchIds", entityIds }),
        [`onNext${capitalized}PageRequested`]: () => dispatch({ type: "fetchNextPage" })
      };
    },
    [dispatch, entityName, state.entities, state.pagination]
  );
};

export default usePagedEntity;
